import React, {useEffect, useState} from "react";
import {Container, Row, Col} from "reactstrap";
import {Controller, useForm} from 'react-hook-form';
import Highlight from "../components/Highlight";
import Loading from "../components/Loading";
import {PageLoader} from "../components/PageLoader";
import {useAuth0, withAuthenticationRequired} from "@auth0/auth0-react";
import UserService from "../services/UserService";
import {toast, ToastContainer} from "react-toastify";
import {useNavigate} from "react-router-dom";
import {ROUTES} from "../config/routes";
import GooglePlacesAutocomplete from "react-google-places-autocomplete";
import { geocodeByPlaceId } from "react-google-places-autocomplete";
import PhoneInput from 'react-phone-input-2';
import 'react-phone-input-2/lib/style.css';

const isNumber = (number) => !isNaN(number) || "Must be a number";
export const MCNumberAndALEI = ({ onDataChange, sharedData }) => {
    // const {register, handleSubmit, formState: {errors}, reset} = useForm();

    let navigate = useNavigate();

    const registerOptions = {
        legalCompanyName: {
            required: "Legal Company Name is required",
            maxLength: {
                value: 200,
                message: "Legal Company Name cannot be more than 200 characters"
            }
        },
        formationDate: {
            required: false
        },
        companyAddress: {
            required: "Company Address is required",
            maxLength: {
                value: 200,
                message: "Company Address cannot be more than 200 characters"
            }
        },
        companyAddressLineTwo: {
            required: false,
            maxLength: {
                value: 200,
                message: "Company Address line 2 cannot be more than 200 characters"
            }
        },
        city: {
            required: "City is required",
            maxLength: {
                value: 100,
                message: "City cannot be more than 100 characters"
            }
        },
        state: {
            required: "State is required"
        },
        zip: {
            required: "ZIP Code is required",
            pattern: {
                value: /^\d{5}(-\d{4})?$/,
                message: "Invalid ZIP code format"
            }
        },
        country: {
            required: false,
            maxLength: {
                value: 100,
                message: "Country cannot be more than 100 characters"
            }
        },
        nlt: {
            required: false,
            maxLength: {
                value: 50,
                message: "NLI cannot be more than 50 characters"
            }
        },
        motorCarrierNumber: {
            required: false,
            maxLength: {
                value: 50,
                message: "MC Number cannot be more than 50 characters"
            }
        },
        dotNumber: {
            required: false,
            maxLength: {
                value: 50,
                message: "DOT Number cannot be more than 50 characters"
            }
        },
        ibrn: {
            required: false,
            maxLength: {
                value: 50,
                message: "IBRN cannot be more than 50 characters"
            }
        },
        smartPrefixField: {
            required: false,
            maxLength: {
                value: 50,
                message: "Smart Prefix Field cannot be more than 50 characters"
            }
        },
        subPrefixField: {
            required: false,
            maxLength: {
                value: 50,
                message: "Sub Prefix Field cannot be more than 50 characters"
            }
        },
        lei: {
            required: false,
            maxLength: {
                value: 50,
                message: "LEI cannot be more than 50 characters"
            }
        },
        companyPhone: {
            required: false,
            maxLength: {
                value: 20,
                message: "Company Phone cannot be more than 20 characters"
            }
        },
        companyEmail: {
            required: false,
            pattern: {
                value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                message: "Invalid email address"
            }
        },
        dnbNumber: {
            required: false,
            maxLength: {
                value: 50,
                message: "D&B Number cannot be more than 50 characters"
            }
        }
    };

    const {
        control,
        register,
        reset,
        handleSubmit,
        formState: { errors },
        getValues,
    } = useForm()

    const [state, setState] = useState({
        showResult: false,
        apiMessage: 'Tickers are loading ....',
        error: null,
    });

    const [farelanesUser, setFarelanesUser] = useState(null);
    const [companyAddressLookup, setCompanyAddressLookup] = useState(null);

    const {
        getAccessTokenSilently,
        user
    } = useAuth0();

    const normalizeCountry = (value) => {
        if (!value) return "";
        const normalized = value.trim();
        if (
            normalized === "US" ||
            normalized === "USA" ||
            normalized === "United States" ||
            normalized === "United States of America"
        ) {
            return "USA";
        }
        return normalized;
    };

    const handleCompanyAddressLookup = (selected) => {
        setCompanyAddressLookup(selected);
        if (!selected?.value?.place_id) return;

        geocodeByPlaceId(selected.value.place_id)
            .then((results) => {
                if (!results?.[0]) return;

                let companyAddress = "";
                let city = "";
                let state = "";
                let country = "";
                let zip = "";

                for (let i = 0; i < results[0].address_components.length; i++) {
                    const comp = results[0].address_components[i];
                    const types = comp.types || [];

                    if (types.includes("street_number")) {
                        companyAddress = comp.long_name;
                    }
                    if (types.includes("route")) {
                        companyAddress = (companyAddress ? companyAddress + " " : "") + comp.long_name;
                    }
                    if (types.includes("locality")) {
                        city = comp.long_name;
                    }
                    if (types.includes("administrative_area_level_1")) {
                        state = comp.short_name;
                    }
                    if (types.includes("country")) {
                        country = normalizeCountry(comp.long_name || comp.short_name || "");
                    }
                    if (types.includes("postal_code")) {
                        zip = comp.short_name;
                    }
                    if (types.includes("postal_code_suffix")) {
                        zip = zip ? `${zip}-${comp.short_name}` : comp.short_name;
                    }
                }

                const currentValues = getValues();

                setFarelanesUser((prev) => ({
                    ...(prev || {}),
                    ...currentValues,
                    companyAddress: companyAddress || currentValues.companyAddress || prev?.companyAddress || "",
                    city: city || currentValues.city || prev?.city || "",
                    state: state || currentValues.state || prev?.state || "",
                    zip: zip || currentValues.zip || prev?.zip || "",
                    country: normalizeCountry(country || currentValues.country || prev?.country || "") || "USA",
                }));
            })
            .catch((e) => console.error(e));
    };

    const callApi = async () => {
        try {
            const token = await getAccessTokenSilently();
            UserService.me(token, user).then(
                response => {
                    console.log('API Response:', response.data);
                    setState({
                        ...state,
                        showResult: true,
                        apiMessage: response.data,
                        farelanesUser: {firstName: 'Frank', lastName: 'Murphy'}
                    });
                    setFarelanesUser({
                        legalCompanyName: response.data.legalCompanyName || '',
                        formationDate: response.data.formationDate || '',
                        companyAddress: response.data.companyAddress || '',
                        companyAddressLineTwo: response.data.companyAddressLineTwo || '',
                        // COMPANY ID address should be independent from Profile address,
                        // so only use the dedicated company fields here.
                        city: response.data.companyCity || '',
                        state: response.data.companyState || '',
                        zip: response.data.companyZip || '',
                        country: normalizeCountry(response.data.companyCountry || response.data.country || "") || "USA",
                        nlt: response.data.companyNli || response.data.nlt || '',
                        motorCarrierNumber: response.data.motorCarrierNumber || '',
                        dotNumber: response.data.dotNumber || '',
                        ibrn: response.data.ibrnNumber || response.data.ibrn || '',
                        smartPrefixField: response.data.smartPrefixField || '',
                        subPrefixField: response.data.subPrefixField || '',
                        lei: response.data.lei || '',
                        companyPhone: response.data.companyPhone || '',
                        companyEmail: response.data.companyEmail || '',
                        dnbNumber: response.data.dnbNumber || ''
                    })
                }
            );
        } catch (error) {
            setState({
                ...state,
                error: error.error,
            });
        }
    };

    async function onSubmit(data) {
        // display form data on submit
        const token = await getAccessTokenSilently();
        setState({
            ...state,
            showResult: false
        });
        const payload = {
            legalCompanyName: data.legalCompanyName,
            formationDate: data.formationDate,
            companyAddress: data.companyAddress,
            companyAddressLineTwo: data.companyAddressLineTwo,
            city: data.city,
            state: data.state,
            zip: data.zip,
            country: normalizeCountry(data.country) || "USA",
            nlt: data.nlt,
            motorCarrierNumber: data.motorCarrierNumber,
            dotNumber: data.dotNumber,
            ibrn: data.ibrn,
            smartPrefixField: data.smartPrefixField,
            subPrefixField: data.subPrefixField,
            lei: data.lei,
            companyPhone: data.companyPhone,
            companyEmail: data.companyEmail,
            dnbNumber: data.dnbNumber,
            sub: user.sub
        };
        console.log('Sending payload:', payload);
        UserService.putUserMCALEI(payload, token).then(response => {
            setState({
                ...state,
                showResult: true
            });
            const toastId = toast("Company information updated successfully", {
                autoClose: 5000,
                onClose: () => console.log("Toast closed")
            });
            // Fallback: ensure toast closes after 5 seconds
            setTimeout(() => {
                toast.dismiss(toastId);
            }, 5000);
            // After company ID is saved, redirect to dashboard
            setTimeout(() => {
                window.dispatchEvent(new CustomEvent('onboardingStatusChanged'));
                if (onDataChange) {
                    onDataChange("Trigger");
                }
                // Redirect to dashboard main tab
                navigate(ROUTES.DASHBOARD, { replace: true });
                // Fallback if navigation is blocked by tab context
                setTimeout(() => {
                    if (window.location.pathname !== ROUTES.DASHBOARD) {
                        window.location.href = ROUTES.DASHBOARD;
                    }
                }, 300);
            }, 1500);
        }).catch(error => {
            setState({
                ...state,
                showResult: true
            });
            const errorToastId = toast.error("Failed to update company information", {
                autoClose: 5000,
                onClose: () => console.log("Error toast closed")
            });
            // Fallback: ensure toast closes after 5 seconds
            setTimeout(() => {
                toast.dismiss(errorToastId);
            }, 5000);
        })
        return true;
    }


    // effect runs on component mount
    useEffect(() => {
        // simulate async api call with set timeout
        callApi().then();
    }, []);

    // effect runs when user state is updated
    useEffect(() => {
        // reset form with user data
        reset(farelanesUser);
    }, [farelanesUser]);

    // return (<section className="AppFonts_Tickers container-fluid">
    return (<section className="my-form">
            <ToastContainer position="top-center"
                            className= "toast-message"
                            autoClose={5000}
                            hideProgressBar={false}
                            newestOnTop={false}
                            closeOnClick
                            rtl={false}
                            pauseOnFocusLoss={false}
                            draggable
                            pauseOnHover={false}
                            theme="light"/>
            {farelanesUser &&
                <div className="my-company-form" id="my-company-form">

                    <form onSubmit={handleSubmit(onSubmit)}>

                        {/* SECTION 1: BUSINESS INFORMATION */}
                        <div className="row mcf-h1">
                            <div className="col-12">
                                <div className="form-label">
                                    BUSINESS INFORMATION
                                </div>
                            </div>
                        </div>

                        <div className="row mb-4" style={{ backgroundColor: "#f4f4f4", padding: "20px", borderRadius: "8px" }}>

                            <div className="col-sm-8 col-md-8 mb-3">
                                <div className="form-outline">
                                    <label className="form-label">Legal Business name*</label>
                                    <input
                                        className="form-control my-text-field"
                                        placeholder="Legal Business name"
                                        {...register("legalCompanyName", registerOptions.legalCompanyName)}
                                    />
                                    <small className="text-danger">{errors?.legalCompanyName && errors.legalCompanyName.message}</small>
                                </div>
                            </div>

                            <div className="col-sm-4 col-md-4 mb-3">
                                <div className="form-outline">
                                    <label className="form-label">Formation date*</label>
                                    <input
                                        type="date"
                                        className="form-control my-text-field"
                                        {...register("formationDate", registerOptions.formationDate)}
                                    />
                                    <small className="text-danger">{errors?.formationDate && errors.formationDate.message}</small>
                                </div>
                            </div>

                            {/* Business Address lookup heading (outside yellow background) */}
                            <div className="col-12 mb-2">
                                <label
                                    className="form-label"
                                    style={{ fontSize: "14px", fontWeight: 600, color: "#414042", marginBottom: "4px" }}
                                >
                                    Business Address lookup
                                </label>
                            </div>

                            {/* Business Address lookup (yellow background like Profile) */}
                            <div className="col-12 mb-3" style={{ backgroundColor: "#FFF9E6", padding: "16px", borderRadius: "8px" }}>
                                <div className="address-search-wrapper" style={{
                                    border: "2px solid #006838",
                                    borderRadius: "8px",
                                    padding: "2px",
                                    backgroundColor: "#ffffff",
                                    boxShadow: "0 2px 4px rgba(0, 104, 56, 0.1)"
                                }}>
                                    <GooglePlacesAutocomplete
                                        placeholder="Start typing an address"
                                        apiKey="AIzaSyDaLZOaYvwAJGsem6pjU8Vg80he7vi2uf0"
                                        selectProps={{
                                            value: companyAddressLookup,
                                            onChange: handleCompanyAddressLookup,
                                            styles: {
                                                control: (provided) => ({
                                                    ...provided,
                                                    border: "none",
                                                    boxShadow: "none",
                                                    minHeight: "auto",
                                                    height: "auto",
                                                    padding: "0.32rem 0.75rem",
                                                    fontSize: "20px",
                                                    fontWeight: "300",
                                                }),
                                                valueContainer: (provided) => ({ ...provided, padding: 0 }),
                                                input: (provided) => ({ ...provided, margin: 0, padding: 0, fontSize: "20px", fontWeight: "300" }),
                                                placeholder: (provided) => ({ ...provided, opacity: 0.7 }),
                                            },
                                        }}
                                        autocompletionRequest={{
                                            types: ["address"],
                                            componentRestrictions: { country: ["us"] }
                                        }}
                                    />
                                </div>
                            </div>

                            {/* Business Address (merged like Profile) */}
                            <div className="col-12 mb-3">
                                <label className="form-label">Business Address*</label>
                                <input
                                    className="form-control my-text-field"
                                    placeholder="Address line 1*"
                                    {...register("companyAddress", registerOptions.companyAddress)}
                                />
                                <small className="text-danger">{errors?.companyAddress && errors.companyAddress.message}</small>
                            </div>

                            <div className="col-12 mb-3">
                                <input
                                    className="form-control my-text-field"
                                    placeholder="Address line 2"
                                    {...register("companyAddressLineTwo", registerOptions.companyAddressLineTwo)}
                                />
                                <small className="text-danger">{errors?.companyAddressLineTwo && errors.companyAddressLineTwo.message}</small>
                            </div>

                            <div className="col-sm-4 col-md-4 mb-3">
                                <input className="form-control my-text-field" placeholder="City*" {...register("city", registerOptions.city)} />
                                <small className="text-danger">{errors?.city && errors.city.message}</small>
                            </div>
                            <div className="col-sm-4 col-md-4 mb-3">
                                <input className="form-control my-text-field" placeholder="State*" {...register("state", registerOptions.state)} />
                                <small className="text-danger">{errors?.state && errors.state.message}</small>
                            </div>
                            <div className="col-sm-4 col-md-4 mb-3">
                                <input className="form-control my-text-field" placeholder="Zipcode*" {...register("zip", registerOptions.zip)} />
                                <small className="text-danger">{errors?.zip && errors.zip.message}</small>
                            </div>

                            <div className="col-sm-4 col-md-4 mb-3">
                                <input className="form-control my-text-field" placeholder="Country" {...register("country", registerOptions.country)} />
                                <small className="text-danger">{errors?.country && errors.country.message}</small>
                            </div>

                            <div className="col-12 mt-2">
                                <label className="form-label">Business Contact Info</label>
                            </div>
                            <div className="col-sm-6 col-md-6 mb-3">
                                <Controller
                                    control={control}
                                    name="companyPhone"
                                    rules={{
                                        required: false,
                                        minLength: {
                                            value: 11,
                                            message: "Enter a valid 10 digits USA phone",
                                        },
                                        maxLength: {
                                            value: 11,
                                            message: "Max Length",
                                        },
                                        validate: (value) =>
                                            !value || isNumber(value),
                                    }}
                                    render={({ field: { ref, ...field } }) => (
                                        <PhoneInput
                                            {...field}
                                            inputExtraProps={{
                                                ref,
                                            }}
                                            country={'us'}
                                            onlyCountries={['us']}
                                            countryCodeEditable={false}
                                            specialLabel={""}
                                        />
                                    )}
                                />
                                <small className="text-danger">
                                    {errors?.companyPhone && errors.companyPhone.message}
                                </small>
                            </div>
                            <div className="col-sm-6 col-md-6 mb-3">
                                <input className="form-control my-text-field" placeholder="E-mail" {...register("companyEmail", registerOptions.companyEmail)} />
                                <small className="text-danger">{errors?.companyEmail && errors.companyEmail.message}</small>
                            </div>
                        </div>

                        {/* SECTION 2: BUSINESS & OPERATIONAL IDENTIFIERS */}
                        <div className="row mb-2">
                            <div className="col-12">
                                <div className="form-label" style={{ fontSize: "16px", fontWeight: 600, color: "#222", marginBottom: "4px" }}>
                                    BUSINESS &amp; OPERATIONAL IDENTIFIERS
                                </div>
                            </div>
                        </div>

                        <div className="row mb-4" style={{ backgroundColor: "#FFF9E6", padding: "20px", borderRadius: "8px" }}>
                            {/* Row 1: IBRN (left), LEI (right) */}
                            <div className="col-sm-6 col-md-6 mb-3">
                                <label className="form-label">IBRN - Int'l Business Registration Number</label>
                                <input className="form-control my-text-field" placeholder="USDOT 0000000" {...register("ibrn", registerOptions.ibrn)} />
                                <small className="text-danger">{errors?.ibrn && errors.ibrn.message}</small>
                            </div>
                            <div className="col-sm-6 col-md-6 mb-3">
                                <label className="form-label">LEI - Legal Entity Identifier</label>
                                <input className="form-control my-text-field" placeholder="0000 0000000000000000 00" {...register("lei", registerOptions.lei)} />
                                <small className="text-danger">{errors?.lei && errors.lei.message}</small>
                            </div>

                            {/* Row 2: Smart prefix, Sub prefix */}
                            <div className="col-sm-6 col-md-6 mb-3">
                                <label className="form-label">Smart Prefix Field</label>
                                <input className="form-control my-text-field" placeholder="00-000-0000" {...register("smartPrefixField", registerOptions.smartPrefixField)} />
                                <small className="text-danger">{errors?.smartPrefixField && errors.smartPrefixField.message}</small>
                            </div>
                            <div className="col-sm-6 col-md-6 mb-3">
                                <label className="form-label">Sub Prefix Field</label>
                                <input className="form-control my-text-field" placeholder="00-000-0000" {...register("subPrefixField", registerOptions.subPrefixField)} />
                                <small className="text-danger">{errors?.subPrefixField && errors.subPrefixField.message}</small>
                            </div>

                            {/* Row 3: DUNS D&B NLI */}
                            <div className="col-sm-6 col-md-6 mb-3">
                                <label className="form-label">DUNS - D&B #</label>
                                <input className="form-control my-text-field" placeholder="00-000-0000" {...register("dnbNumber", registerOptions.dnbNumber)} />
                                <small className="text-danger">{errors?.dnbNumber && errors.dnbNumber.message}</small>
                            </div>
                            <div className="col-sm-6 col-md-6 mb-3">
                                <label className="form-label">NLI - Natural Location Identifier</label>
                                <input className="form-control my-text-field" placeholder="NLT-US-DFW-00000" {...register("nlt", registerOptions.nlt)} />
                                <small className="text-danger">{errors?.nlt && errors.nlt.message}</small>
                            </div>

                            {/* Row 4: US DOT, then MC */}
                            <div className="col-sm-6 col-md-6 mb-3">
                                <label className="form-label">USDOT - DOT #</label>
                                <input className="form-control my-text-field" placeholder="USDOT-0000000" {...register("dotNumber", registerOptions.dotNumber)} />
                                <small className="text-danger">{errors?.dotNumber && errors.dotNumber.message}</small>
                            </div>
                            <div className="col-sm-6 col-md-6 mb-3">
                                <label className="form-label">MC - Motor Carrier #</label>
                                <input className="form-control my-text-field" placeholder="MC-000000-0000" {...register("motorCarrierNumber", registerOptions.motorCarrierNumber)} />
                                <small className="text-danger">{errors?.motorCarrierNumber && errors.motorCarrierNumber.message}</small>
                            </div>
                        </div>

                        {/* Submit Button */}
                        <div className="row">
                            <div className="col-12 text-center">
                                <input
                                    className="button__settings-save"
                                    type="submit"
                                    value="SUBMIT"
                                    style={{
                                        backgroundColor: "#FFB84D",
                                        color: "#fff",
                                        border: "none",
                                        padding: "12px 40px",
                                        fontSize: "16px",
                                        fontWeight: "600",
                                        borderRadius: "6px",
                                        cursor: "pointer",
                                        minWidth: "240px",
                                        whiteSpace: "nowrap"
                                    }}
                                />
                            </div>
                        </div>
                    </form>
                </div>
            }
            {(!farelanesUser || !state.showResult) &&
                <PageLoader />
            }
        </section>
    );
};

export default withAuthenticationRequired(MCNumberAndALEI, {
    onRedirecting: () => <Loading/>,
});
